import { useState, createContext, useMemo, useContext } from "react";
import { Transition } from "@headlessui/react";
import { HeartIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { Text } from "./i18n";
import { useToken } from "./TokenProvider";
import { useRsvp } from "../api";

type NotificationContext = {
  isVisible: boolean;
  show: () => void;
  hide: () => void;
};

const defaultNotificationContext: NotificationContext = {
  isVisible: false,
  show: () => {},
  hide: () => {},
};

const notificationContext = createContext<NotificationContext>(
  defaultNotificationContext
);

notificationContext.displayName = "NotificationContext";

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const memoedContextValues = useMemo<NotificationContext>(() => {
    return {
      isVisible,
      show: () => {
        setIsVisible(true);
      },
      hide: () => {
        setIsVisible(false);
      },
    };
  }, [isVisible, setIsVisible]);

  return (
    <notificationContext.Provider value={memoedContextValues}>
      {children}
    </notificationContext.Provider>
  );
};

export function useNotification() {
  return useContext(notificationContext);
}

export function Notification() {
  const tokenContext = useToken();
  const rsvp = useRsvp(tokenContext.token);

  const notification = useNotification();

  return (
    <>
      <div
        aria-live="assertive"
        className="pointer-events-none fixed z-50 inset-0 flex items-end px-4 py-6 sm:items-start sm:p-6"
      >
        <div className="flex w-full flex-col items-center space-y-4 sm:items-end">
          <Transition show={notification.isVisible}>
            <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white ring-1 shadow-lg ring-black/5 transition data-closed:opacity-0 data-enter:transform data-enter:duration-300 data-enter:ease-out data-closed:data-enter:translate-y-2 data-leave:duration-100 data-leave:ease-in data-closed:data-enter:sm:translate-x-2 data-closed:data-enter:sm:translate-y-0">
              <div className="p-4">
                <div className="flex items-start">
                  <div className="shrink-0">
                    <HeartIcon
                      aria-hidden="true"
                      className="size-6 text-gray-400"
                    />
                  </div>
                  <div className="ml-3 w-0 flex-1 pt-0.5">
                    <p className="text-sm font-medium text-gray-900">
                      <Text id="notification.saved" />
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      <Text
                        id={
                          rsvp.data?.guests.confirmed
                            ? rsvp.data.guests.partner
                              ? "notification.saved.description.confirmed.plural"
                              : "notification.saved.description.confirmed.singular"
                            : "notification.saved.description.declined"
                        }
                      />
                    </p>
                    <div className="mt-3 flex space-x-7">
                      <button
                        type="button"
                        onClick={notification.hide}
                        className="rounded-md bg-white text-sm font-medium text-gray-700 hover:text-gray-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden"
                      >
                        <Text id="notification.dismiss" />
                      </button>
                    </div>
                  </div>
                  <div className="ml-4 flex shrink-0">
                    <button
                      type="button"
                      onClick={notification.hide}
                      className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden"
                    >
                      <span className="sr-only">
                        <Text id="notification.close" />
                      </span>
                      <XMarkIcon aria-hidden="true" className="size-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </>
  );
}
